import { useEffect, useState } from 'react';
import { useGlobalSettings } from '../../hooks/useGlobalSettings';
import { useAvatar } from '../../hooks/useAvatar';
import { useScreensaverData } from '../../hooks/pages/useScreensaverData';
import Animation from '../common/Animation';
import AnimatedWordsMarkdown from '../common/AnimatedWordsMarkdown';

const LAYOUTS = {
  //~1920×1080
  FHD: {
    logo:   { top: 48,  left: 48,  height: 64 },
    avatar: { top: '53%', left: '10%', width: 500, height: 1000, scale: '170%' },
    box:    { top: '10%', left: '12%', width: '80%', paddingY: 80, paddingX: 48, radiusTL: 150, radiusBR: 150 },
    text:   { fontSizeClass: 'text-5xl' },
  },

  //~3840×2160
  UHD: {
    logo:   { top: 96,  left: 96,  height: 128 },
    avatar: { top: '79%', left: '10%', width: 500, height: 1000, scale: '330%' },
    box:    { top: '10%', left: '13%', width: '75%', paddingY: 160, paddingX: 96, radiusTL: 300, radiusBR: 300 },
    text:   { fontSizeClass: 'text-8xl' },
  },

  // 16:9 Landscape, ~2560×1440 (WQHD) 
  QHD: {
    logo:   { top: 72,  left: 72,  height: 96 },
    avatar: { top: '66%', left: '10%', width: 500, height: 1000, scale: '230%' },
    box:    { top: '15%', left: '12%', width: '78%', paddingY: 120, paddingX: 72, radiusTL: 220, radiusBR: 220 },
    text:   { fontSizeClass: 'text-6xl' },
  },
};

function detectProfileSmart() {
  if (typeof window === 'undefined') return 'FHD';

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;

  const effLong = Math.max(vw, vh) * dpr;   
  const effShort = Math.min(vw, vh) * dpr;  

  const p = new URLSearchParams(window.location.search).get('profile');
  if (p && LAYOUTS[p]) return p;

  // Landscape (16:9)
  if ((effLong >= 4000 && effShort >= 2100) || (effLong >= 3800 && effShort >= 2100)) return 'UHD';
  if (effLong >= 2500 && effShort >= 1400) return 'QHD';
  return 'FHD';
}

export default function ScreensaverFlipped({
  station,
  theme,
  rotations = {},
  origins = {},
}) {
  const { data: pageData, error: pageDataError } = useScreensaverData(station);
  const { globalSettings, error: globalSettingsError } = useGlobalSettings();
  const { avatarData, error: avatarDataError } = useAvatar('Bildschirmschoner');

  const [profile, setProfile] = useState(detectProfileSmart);
  useEffect(() => {
    const onResize = () => setProfile(detectProfileSmart());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  if (globalSettingsError || pageDataError || avatarDataError) {
    return <div>Fehler: {globalSettingsError || pageDataError || avatarDataError}</div>;
  }
  if (!globalSettings || !pageData || !avatarData) {
    return <div>Laden...</div>;
  }

  const { text } = pageData;
  const { logoUrl } = globalSettings;
  const avatar = avatarData[0]?.animation;

  const toDeg = (v) => (typeof v === 'number' ? v : parseFloat(v || 0));
  const ori   = (k, fallback='center') => origins?.[k] || fallback;
  const rootAngle = ((toDeg(rotations.root) % 360) + 360) % 360;

  const rootBase = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
    overflow: 'hidden',
    position: 'fixed',
    inset: 0,
  };

  let rootStyle = { ...rootBase };
  if (rootAngle === 90) {
    rootStyle = { ...rootBase, width: '100vh', height: '100vw', transformOrigin: origins?.root || 'top left', transform: 'rotate(90deg) translateY(-100%)' };
  } else if (rootAngle === 270) {
    rootStyle = { ...rootBase, width: '100vh', height: '100vw', transformOrigin: origins?.root || 'top left', transform: 'rotate(270deg) translateX(-100%)' };
  } else if (rootAngle === 180) {
    rootStyle = { ...rootBase, transformOrigin: ori('root'), transform: 'rotate(180deg)' };
  } else {
    rootStyle = { ...rootBase, transformOrigin: ori('root'), transform: `rotate(${rootAngle}deg)` };
  }

  const cfg = LAYOUTS[profile] || LAYOUTS.FHD;

  return (
    <div className="fixed inset-0 overflow-hidden" style={rootStyle}>
      <div className="relative w-full h-full">
        {/* Logo */}
        <img
          src={logoUrl}
          alt="Logo"
          style={{
            position: 'absolute',
            top: cfg.logo.top,
            left: cfg.logo.left,
            height: cfg.logo.height,
            rotate: typeof rotations.logo === 'number' ? `${rotations.logo}deg` : (rotations.logo || '0deg'),
            transformOrigin: ori('logo'),
          }}
        />

        {/* Avatar */}
        <div
          style={{
            position: 'absolute',
            top: cfg.avatar.top,
            left: cfg.avatar.left,
            width: cfg.avatar.width,
            height: cfg.avatar.height,
            ...(cfg.avatar.translateY ? { translate: `0 ${cfg.avatar.translateY}` } : {}),
            rotate: typeof rotations.avatar === 'number' ? `${rotations.avatar}deg` : (rotations.avatar || '0deg'),
            transformOrigin: ori('avatar', 'bottom left'),
            ...(cfg.avatar.scale ? { scale: cfg.avatar.scale } : {}),
          }}
        >
          <Animation src={avatar} className="w-full h-full object-contain" />
        </div>

        {/* Textbox */}
        <div
          style={{
            position: 'absolute',
            top: cfg.box.top,
            left: cfg.box.left,
            width: cfg.box.width,
            padding: `${cfg.box.paddingY}px ${cfg.box.paddingX}px`,
            margin: 0,
            backgroundColor: theme.screensaverForegroundColor,
            borderTopLeftRadius: cfg.box.radiusTL,
            borderBottomRightRadius: cfg.box.radiusBR,
            rotate: typeof rotations.box === 'number' ? `${rotations.box}deg` : (rotations.box || '0deg'),
            transformOrigin: ori('box'),
          }}
        >
          <div
            className={cfg.text.fontSizeClass}
            style={{
              rotate: typeof rotations.text === 'number' ? `${rotations.text}deg` : (rotations.text || '0deg'),
              transformOrigin: ori('text'),
            }}
          >
            <AnimatedWordsMarkdown text={text} />
          </div>
        </div>
      </div>
    </div>
  );
}